// src/pages/dashboard/DashboardGlobal.jsx
import React, { useEffect, useState } from "react";
import {
    Button, Card, Col, Nav, ProgressBar, Row, Spinner, Alert, Dropdown, Badge
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { formatNumber, formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DashboardGlobal() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [alertes, setAlertes] = useState([]);
    const [topClients, setTopClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriode, setSelectedPeriode] = useState("6m");
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        fetchAllData();

        const interval = setInterval(fetchAllData, 120000);
        const clock = setInterval(() => setCurrentTime(new Date()), 60000);

        return () => {
            clearInterval(interval);
            clearInterval(clock);
        };
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchAllData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

            const [statsRes, alertesRes, topRes] = await Promise.all([
                fetch(`${API_BASE_URL}/dashboard.php?action=global`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/dashboard.php?action=alertes`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/dashboard.php?action=top_clients&limit=5`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const statsData = await statsRes.json();
            const alertesData = await alertesRes.json();
            const topData = await topRes.json();

            if (statsData.success) setStats(statsData.data);
            if (alertesData.success) setAlertes(alertesData.data || []);
            if (topData.success) setTopClients(topData.data || []);
        } catch (err) {
            console.error("Erreur dashboard:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getGreeting = () => {
        const h = currentTime.getHours();
        if (h < 12) return "Bonjour";
        if (h < 18) return "Bon après-midi";
        return "Bonsoir";
    };

    const getInitials = () => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const p = user.prenom || user.prenom_utilisateur || "";
            const n = user.nom || user.nom_utilisateur || "";
            return (p.charAt(0) + n.charAt(0)).toUpperCase() || "U";
        } catch { return "U"; }
    };

    const getUserName = () => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const p = user.prenom || user.prenom_utilisateur || "";
            const n = user.nom || user.nom_utilisateur || "";
            return `${p} ${n}`.trim() || "Utilisateur";
        } catch { return "Utilisateur"; }
    };

    const chartColors = {
        primary: COLORS.primary,
        secondary: COLORS.secondary,
        success: COLORS.success,
        danger: COLORS.danger,
        warning: COLORS.warning,
        info: COLORS.info,
        palette: [
            COLORS.primary,
            COLORS.secondary,
            COLORS.success,
            COLORS.warning,
            COLORS.info,
            '#8b5cf6',
            '#06b6d4',
            COLORS.danger
        ]
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !stats) {
       return (
                         <>
                             <Header />
                             <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                                 <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                     <div className="text-center" style={{ maxWidth: '400px' }}>
                                         <div className="loading-ring-wrapper mb-4">
                                             <div className="loading-ring"></div>
                                             <div className="loading-ring-inner"></div>
                                             <i className="ri-loader-4-fill loading-icon"></i>
                                         </div>
                                         <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                             Chargement de la page...
                                         </h5>
                                         <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                         <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                             <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                                 <div
                                                     className="progress-bar progress-bar-striped progress-bar-animated"
                                                     style={{
                                                         width: '100%',
                                                         background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                         backgroundSize: '200% 100%',
                                                         animation: 'shimmer 1.5s linear infinite'
                                                     }}
                                                 />
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                             <Footer />
                             <style>{`
                                 .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                                 .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                                 .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                                 .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                                 @keyframes spin { to { transform: rotate(360deg); } }
                                 @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                                 @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                             `}</style>
                         </>
                     );
    }

    if (error && !stats) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchAllData} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const contrats = stats?.contrats || {};
    const caisse = stats?.caisse || {};
    const clients = stats?.clients || {};
    const employes = stats?.employes || {};
    const notifications = stats?.notifications || {};
    const graphiques = stats?.graphiques || {};

    /* ============================================================
       GRAPHIQUES APEXCHARTS
       ============================================================ */
    // 1. Évolution contrats (Area)
    const seriesContrats = [{
        name: 'Contrats',
        data: graphiques.contrats_par_mois?.map(m => m.contrats) || Array(6).fill(0)
    }];

    const optionsContrats = {
        chart: {
            type: 'area',
            height: 300,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        colors: [chartColors.primary],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.15,
                stops: [0, 90, 100]
            }
        },
        stroke: { curve: 'smooth', width: 3 },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: graphiques.contrats_par_mois?.map(m => m.mois) || [],
            labels: { style: { fontSize: '11px', fontWeight: 500, colors: '#64748b' } }
        },
        yaxis: {
            labels: { style: { fontSize: '11px', colors: '#64748b' } },
            forceNiceScale: true
        },
        markers: {
            size: 5,
            colors: [chartColors.primary],
            strokeColors: '#fff',
            strokeWidth: 2,
            hover: { size: 7 }
        },
        tooltip: { y: { formatter: (v) => `${v} contrat(s)` } }
    };

    // 2. Évolution caisse (Bar)
    const seriesCaisse = [{
        name: 'Montant',
        data: graphiques.caisse_par_mois?.map(m => m.montant) || Array(6).fill(0)
    }];

    const optionsCaisse = {
        chart: {
            type: 'bar',
            height: 300,
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        colors: [chartColors.danger],
        plotOptions: {
            bar: {
                columnWidth: '55%',
                borderRadius: 6,
                borderRadiusApplication: 'end'
            }
        },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: graphiques.caisse_par_mois?.map(m => m.mois) || [],
            labels: { style: { fontSize: '11px', fontWeight: 500, colors: '#64748b' } }
        },
        yaxis: {
            labels: {
                formatter: (v) => formatCurrency(v),
                style: { fontSize: '10px', colors: '#64748b' }
            }
        },
        tooltip: { y: { formatter: (v) => formatCurrency(v) } },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.3,
                gradientToColors: ['#dc2626'],
                stops: [0, 100]
            }
        }
    };

    // 3. Clients par mois (Bar)
    const seriesClients = [{
        name: 'Clients',
        data: graphiques.clients_par_mois?.map(m => m.clients) || Array(6).fill(0)
    }];

    const optionsClients = {
        chart: {
            type: 'bar',
            height: 300,
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        colors: [chartColors.info],
        plotOptions: {
            bar: {
                columnWidth: '55%',
                borderRadius: 6,
                borderRadiusApplication: 'end'
            }
        },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: graphiques.clients_par_mois?.map(m => m.mois) || [],
            labels: { style: { fontSize: '11px', fontWeight: 500, colors: '#64748b' } }
        },
        yaxis: {
            labels: { style: { fontSize: '11px', colors: '#64748b' } },
            forceNiceScale: true
        },
        tooltip: { y: { formatter: (v) => `${v} client(s)` } }
    };

    // 4. Répartition contrats (Donut)
    const seriesRepartition = [
        contrats.actifs || 0,
        contrats.expires || 0,
        contrats.a_venir || 0
    ];
    const hasRepartitionData = seriesRepartition.reduce((a, b) => a + b, 0) > 0;

    const optionsRepartition = {
        chart: { type: 'donut', height: 280, fontFamily: 'Inter, system-ui, sans-serif' },
        labels: ['Actifs', 'Expirés', 'À venir'],
        colors: [chartColors.success, chartColors.danger, chartColors.warning],
        legend: { position: 'bottom', fontSize: '11px', offsetY: 5 },
        tooltip: { y: { formatter: (v) => `${v} contrat(s)` } },
        plotOptions: {
            pie: {
                donut: {
                    size: '68%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '14px',
                            fontWeight: 600,
                            formatter: () => contrats.total || 0
                        }
                    }
                }
            }
        }
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-dashboard-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Statistiques globales</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i>
                                        {currentTime.toLocaleDateString('fr-FR', {
                                            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                                        })}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-file-copy-line"></i> {contrats.actifs || 0} contrats actifs
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-user-3-line"></i> {clients.total_clients || 0} clients
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-team-line"></i> {employes.total || 0} employés
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <div className="year-selector-charte-header">
                                <button
                                    className={`year-btn-header-charte ${selectedPeriode === '6m' ? 'active' : ''}`}
                                    onClick={() => setSelectedPeriode('6m')}
                                >
                                    6 mois
                                </button>
                                <button
                                    className={`year-btn-header-charte ${selectedPeriode === '12m' ? 'active' : ''}`}
                                    onClick={() => setSelectedPeriode('12m')}
                                >
                                    12 mois
                                </button>
                            </div>
                            <Button className="btn-modern-primary" onClick={fetchAllData} disabled={loading}>
                                <i className={`ri-refresh-line me-1 ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== HERO WELCOME ===== */}
                    <Card className="hero-welcome-card mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="hero-avatar-charte">
                                            {getInitials()}
                                        </div>
                                        <div>
                                            <div className="hero-greeting-charte">{getGreeting()},</div>
                                            <div className="hero-name-charte">{getUserName()}</div>
                                            <div className="hero-sub-charte">
                                                Voici un résumé de votre activité
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="hero-stats-charte">
                                        <div className="hero-stat-item-charte">
                                            <span className="hero-stat-value-charte">
                                                {contrats.expirant_bientot || 0}
                                            </span>
                                            <span className="hero-stat-label-charte">Expirent bientôt</span>
                                        </div>
                                        <div className="hero-stat-divider"></div>
                                        <div className="hero-stat-item-charte">
                                            <span className="hero-stat-value-charte">
                                                {notifications.aujourd_hui || 0}
                                            </span>
                                            <span className="hero-stat-label-charte">Notifications du jour</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== ALERTES ===== */}
                    {alertes.length > 0 && (
                        <Row className="g-3 mb-4">
                            {alertes.map((a, i) => (
                                <Col md={6} xl={6} key={i}>
                                    <div
                                        className={`alerte-global alerte-${a.type}`}
                                        // onClick={() => a.link && navigate(a.link)}
                                    >
                                        <div className="alerte-icon">
                                            <i className={a.icon}></i>
                                        </div>
                                        <div className="alerte-body">
                                            <div className="alerte-title">{a.title}</div>
                                            <div className="alerte-message">{a.message}</div>
                                        </div>
                                        {a.link && <i className="ri-arrow-right-s-line alerte-arrow"></i>}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-file-copy-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Contrats actifs</span>
                                    <h3 className="kpi-value-charte">{contrats.actifs || 0}</h3>
                                    <span className="kpi-unit-charte">sur {contrats.total || 0} au total</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-bank-card-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Reste à payer (caisse)</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.danger }}>
                                        {formatCurrency(caisse.reste_total || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">Taux {caisse.taux_recouvrement || 0}% recouvré</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-user-3-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.info }}>
                                        {clients.total_clients || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">{clients.clients_debiteurs || 0} débiteur(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-team-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Employés</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.success }}>
                                        {employes.total || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">{employes.actifs || 0} actif(s)</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== OBJECTIF CARD (Reste à payer) ===== */}
                    <Card className="objectif-card-charte mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="objectif-icon-charte">
                                            <i className="ri-money-dollar-circle-line"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="small fw-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                                    Taux de recouvrement global
                                                </span>
                                                <span className="objectif-pourcentage-charte">
                                                    {caisse.taux_recouvrement || 0}%
                                                </span>
                                            </div>
                                            <div className="progress-objectif-charte">
                                                <div
                                                    className="progress-bar-objectif-charte"
                                                    style={{ width: `${Math.min(caisse.taux_recouvrement || 0, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-md-end text-center">
                                        <span className="small d-block" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                            Total versé
                                        </span>
                                        <span className="fw-bold" style={{ color: 'white', fontSize: '1.3rem' }}>
                                            {formatCurrency(caisse.total_verse || 0)}
                                        </span>
                                        <small className="d-block" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                            sur {formatCurrency(caisse.total_decaissements || 0)}
                                        </small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== GRAPHIQUE CONTRATS + DONUT ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-line-chart-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Évolution des contrats</h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-calendar-line"></i> {selectedPeriode === '6m' ? '6 derniers mois' : '12 derniers mois'}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart
                                        series={seriesContrats}
                                        options={optionsContrats}
                                        type="area"
                                        height={330}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white"></i>
                                        <h6 className="fw-semibold mb-0 text-white">Statuts contrats</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {hasRepartitionData ? (
                                        <ReactApexChart
                                            series={seriesRepartition}
                                            options={optionsRepartition}
                                            type="donut"
                                            height={280}
                                        />
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte"
                                                style={{
                                                    background: `linear-gradient(135deg, ${COLORS.secondary}15, ${COLORS.secondary}05)`,
                                                    color: COLORS.secondary
                                                }}>
                                                <i className="ri-pie-chart-2-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucune donnée</h6>
                                            <p className="text-muted small mb-0">Aucun contrat enregistré</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== CAISSE + CLIENTS ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="6">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-bar-chart-2-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Évolution caisse</h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-money-cfa-line"></i> Montants décaissés
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart
                                        series={seriesCaisse}
                                        options={optionsCaisse}
                                        type="bar"
                                        height={300}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="6">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-user-add-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Nouveaux clients</h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-line-chart-line"></i> Par mois
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart
                                        series={seriesClients}
                                        options={optionsClients}
                                        type="bar"
                                        height={300}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== TOP CLIENTS + ACTIVITÉ ===== */}
                    <Row className="g-3 mb-4">
                        {/* Top clients */}
                        <Col xl="6" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-trophy-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Top {topClients.length || 5} clients</h6>
                                        </div>
                                        <Button
                                            className="btn-header-light"
                                            size="sm"
                                            onClick={() => navigate('/ventes/clients/liste')}
                                        >
                                            Voir tous <i className="ri-arrow-right-line ms-1"></i>
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <table className="table-charte mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4" style={{ width: '50px' }}>#</th>
                                                    <th>Client</th>
                                                    <th className="text-end">Contrats</th>
                                                    <th className="text-end pe-4">Employés</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topClients.length > 0 ? topClients.map((c, i) => (
                                                    <tr
                                                        key={c.id_client}
                                                        onClick={() => navigate(`/ventes/clients/${c.id_client}`)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td className="ps-4">
                                                            <div
                                                                className="top-rank-badge"
                                                                style={{
                                                                    background: i === 0
                                                                        ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                                                        : i === 1
                                                                        ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                                                        : i === 2
                                                                        ? 'linear-gradient(135deg, #b45309, #92400e)'
                                                                        : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
                                                                    color: i <= 2 ? 'white' : '#64748b'
                                                                }}
                                                            >
                                                                {i + 1}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                                {c.raison_sociale}
                                                            </div>
                                                            <small className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                                {c.code_client}
                                                            </small>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="stat-badge-primary">{c.nb_contrats}</span>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <span className="stat-badge-secondary">{c.nb_employes}</span>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="4">
                                                            <div className="empty-state-charte py-4">
                                                                <div className="empty-state-icon-charte"
                                                                    style={{
                                                                        background: `linear-gradient(135deg, ${COLORS.warning}15, ${COLORS.warning}05)`,
                                                                        color: COLORS.warning,
                                                                        width: '60px',
                                                                        height: '60px',
                                                                        fontSize: '1.5rem'
                                                                    }}>
                                                                    <i className="ri-trophy-line"></i>
                                                                </div>
                                                                <p className="text-muted small mb-0">Aucun client avec contrats</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Activité récente */}
                        <Col xl="6" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-history-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Activité récente</h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-time-line"></i> 10 dernières
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div className="activite-list">
                                        {stats?.activite_recente?.length > 0 ? (
                                            stats.activite_recente.map((a, i) => (
                                                <div
                                                    key={i}
                                                    className="activite-item"
                                                    // onClick={() => a.link && navigate(a.link)}
                                                    style={{ cursor: a.link ? 'pointer' : 'default' }}
                                                >
                                                    <div className={`activite-icon activite-${a.color}`}>
                                                        <i className={a.icon}></i>
                                                    </div>
                                                    <div className="activite-body">
                                                        <div className="activite-title">{a.title}</div>
                                                        <div className="activite-subtitle">{a.subtitle}</div>
                                                    </div>
                                                    <div className="activite-date">
                                                        {formatDate(a.date)}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state-charte py-4">
                                                <div className="empty-state-icon-charte"
                                                    style={{
                                                        background: `linear-gradient(135deg, ${COLORS.info}15, ${COLORS.info}05)`,
                                                        color: COLORS.info,
                                                        width: '60px',
                                                        height: '60px',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                    <i className="ri-history-line"></i>
                                                </div>
                                                <p className="text-muted small mb-0">Aucune activité récente</p>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== DÉTAILS RAPIDES ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="mini-kpi-global">
                                <div className="mini-kpi-icon" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-file-list-3-line"></i>
                                </div>
                                <div>
                                    <div className="mini-kpi-label">Total contrats</div>
                                    <div className="mini-kpi-value">{contrats.total || 0}</div>
                                    <div className="mini-kpi-sub">{contrats.cdd || 0} CDD • {contrats.cdi || 0} CDI</div>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="mini-kpi-global">
                                <div className="mini-kpi-icon" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                    <i className="ri-alarm-warning-line"></i>
                                </div>
                                <div>
                                    <div className="mini-kpi-label">Expirent bientôt</div>
                                    <div className="mini-kpi-value">{contrats.expirant_bientot || 0}</div>
                                    <div className="mini-kpi-sub">Dans les 7 jours</div>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="mini-kpi-global">
                                <div className="mini-kpi-icon" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                    <i className="ri-secure-payment-line"></i>
                                </div>
                                <div>
                                    <div className="mini-kpi-label">Total versé</div>
                                    <div className="mini-kpi-value">{formatCurrency(caisse.total_verse || 0)}</div>
                                    <div className="mini-kpi-sub">{caisse.nb_operations || 0} opération(s)</div>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="mini-kpi-global">
                                <div className="mini-kpi-icon" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                    <i className="ri-notification-3-line"></i>
                                </div>
                                <div>
                                    <div className="mini-kpi-label">Notifications</div>
                                    <div className="mini-kpi-value">{notifications.envoyes || 0}</div>
                                    <div className="mini-kpi-sub">{notifications.aujourd_hui || 0} aujourd'hui</div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FOOTER INFO ===== */}
                    <div className="footer-info-global">
                        <i className="ri-information-line me-1"></i>
                        Dernière mise à jour : {stats?.generated_at ? formatDate(stats.generated_at) : '—'}
                        <span className="mx-2">•</span>
                        Rafraîchissement automatique toutes les 2 minutes
                    </div>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    text-transform: capitalize;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== YEAR SELECTOR ===== */
                .year-selector-charte-header {
                    display: inline-flex; background: #f1f5f9;
                    border-radius: 50px; padding: 3px;
                }
                .year-btn-header-charte {
                    border: none; background: transparent;
                    padding: 0.4rem 1rem;
                    border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .year-btn-header-charte:hover { color: var(--primary); }
                .year-btn-header-charte.active {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    color: white;
                    box-shadow: 0 2px 6px rgba(41,90,167,0.3);
                }

                /* ===== HERO WELCOME ===== */
                .hero-welcome-card {
                    background: linear-gradient(135deg, ${COLORS.primary}08 0%, ${COLORS.secondary}08 100%);
                    border: 1px solid ${COLORS.primary}15;
                    border-radius: 16px;
                    overflow: hidden;
                    position: relative;
                }
                .hero-welcome-card::before {
                    content: '';
                    position: absolute;
                    top: -50%; right: -10%;
                    width: 300px; height: 300px;
                    background: radial-gradient(circle, ${COLORS.secondary}20 0%, transparent 70%);
                    border-radius: 50%;
                    pointer-events: none;
                }
                .hero-avatar-charte {
                    width: 64px; height: 64px;
                    border-radius: 16px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; font-weight: 800;
                    box-shadow: 0 6px 20px rgba(41,90,167,0.3);
                    flex-shrink: 0;
                }
                .hero-greeting-charte {
                    font-size: 0.85rem; color: #94a3b8;
                    font-weight: 500;
                }
                .hero-name-charte {
                    font-size: 1.4rem; font-weight: 800;
                    color: var(--primary-dark);
                    letter-spacing: -0.3px;
                }
                .hero-sub-charte {
                    font-size: 0.8rem; color: #64748b;
                    margin-top: 3px;
                }
                .hero-stats-charte {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 1.5rem;
                }
                .hero-stat-item-charte {
                    text-align: center;
                }
                .hero-stat-value-charte {
                    display: block;
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: var(--primary);
                    line-height: 1;
                }
                .hero-stat-label-charte {
                    font-size: 0.7rem;
                    color: #94a3b8;
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.4px;
                    margin-top: 4px;
                    display: block;
                }
                .hero-stat-divider {
                    width: 1px;
                    height: 40px;
                    background: linear-gradient(180deg, transparent, #e2e8f0, transparent);
                }

                /* ===== ALERTES ===== */
                .alerte-global {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 0.9rem 1rem;
                    background: white;
                    border-radius: 12px;
                    border-left: 4px solid;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .alerte-global:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
                }
                .alerte-danger  { border-left-color: var(--danger); }
                .alerte-warning { border-left-color: var(--warning); }
                .alerte-info    { border-left-color: var(--info); }
                .alerte-icon {
                    width: 40px; height: 40px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.15rem; flex-shrink: 0;
                }
                .alerte-danger  .alerte-icon { background: ${COLORS.danger}15; color: var(--danger); }
                .alerte-warning .alerte-icon { background: ${COLORS.warning}15; color: var(--warning); }
                .alerte-info    .alerte-icon { background: ${COLORS.info}15; color: var(--info); }
                .alerte-body { flex: 1; min-width: 0; }
                .alerte-title {
                    font-weight: 700; color: #1e293b;
                    font-size: 0.82rem;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .alerte-message {
                    font-size: 0.72rem; color: #94a3b8;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .alerte-arrow { color: #cbd5e1; font-size: 1.2rem; flex-shrink: 0; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== OBJECTIF ===== */
                .objectif-card-charte {
                    background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.secondary});
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.25);
                    overflow: hidden;
                    position: relative;
                }
                .objectif-icon-charte {
                    width: 52px; height: 52px; border-radius: 14px;
                    background: rgba(255,255,255,0.15);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                    flex-shrink: 0;
                }
                .objectif-pourcentage-charte {
                    background: white; color: var(--primary);
                    padding: 0.2rem 0.75rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                }
                .progress-objectif-charte {
                    height: 8px; border-radius: 6px;
                    background: rgba(255,255,255,0.2);
                    overflow: hidden;
                }
                .progress-bar-objectif-charte {
                    height: 100%; border-radius: 6px;
                    background: linear-gradient(90deg, #ffffff, rgba(255,255,255,0.7));
                    transition: width 0.6s ease;
                    box-shadow: 0 2px 8px rgba(255,255,255,0.3);
                }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-header-light {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    border-radius: 50px;
                    font-size: 0.72rem;
                    font-weight: 500;
                    padding: 0.3rem 0.8rem;
                    transition: all 0.2s ease;
                }
                .btn-header-light:hover {
                    background: rgba(255,255,255,0.25);
                    color: white;
                    border-color: rgba(255,255,255,0.5);
                }

                /* ===== TABLE ===== */
                .table-charte {
                    width: 100%;
                    border-collapse: collapse;
                }
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr:last-child td { border-bottom: none; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                .top-rank-badge {
                    width: 32px; height: 32px;
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 800; font-size: 0.8rem;
                }
                .stat-badge-primary {
                    display: inline-flex;
                    align-items: center; justify-content: center;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                    min-width: 36px;
                }
                .stat-badge-secondary {
                    display: inline-flex;
                    align-items: center; justify-content: center;
                    background: ${COLORS.secondary}15;
                    color: var(--secondary);
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                    min-width: 36px;
                }

                /* ===== ACTIVITÉ ===== */
                .activite-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                    max-height: 380px;
                    overflow-y: auto;
                }
                .activite-item {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 0.7rem 0.85rem;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                }
                .activite-item:hover {
                    background: #f8fafc;
                    transform: translateX(4px);
                }
                .activite-icon {
                    width: 38px; height: 38px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1rem; flex-shrink: 0;
                }
                .activite-primary { background: ${COLORS.primary}15; color: var(--primary); }
                .activite-info    { background: ${COLORS.info}15;    color: var(--info); }
                .activite-success { background: ${COLORS.success}15; color: var(--success); }
                .activite-warning { background: ${COLORS.warning}15; color: var(--warning); }
                .activite-body { flex: 1; min-width: 0; }
                .activite-title {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.8rem;
                }
                .activite-subtitle {
                    font-size: 0.72rem;
                    color: #94a3b8;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .activite-date {
                    font-size: 0.68rem;
                    color: #cbd5e1;
                    font-weight: 500;
                    flex-shrink: 0;
                }

                /* ===== MINI KPI ===== */
                .mini-kpi-global {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .mini-kpi-global:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
                }
                .mini-kpi-icon {
                    width: 42px; height: 42px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.2rem; flex-shrink: 0;
                }
                .mini-kpi-label {
                    font-size: 0.68rem;
                    color: #94a3b8;
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.4px;
                }
                .mini-kpi-value {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-top: 2px;
                }
                .mini-kpi-sub {
                    font-size: 0.68rem;
                    color: #94a3b8;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== FOOTER INFO ===== */
                .footer-info-global {
                    text-align: center;
                    font-size: 0.75rem;
                    color: #94a3b8;
                    padding: 1rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db;
                    background: white;
                    padding: 0.45rem 1.3rem;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary);
                    color: var(--secondary);
                    background: #f0fbff;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none;
                    color: white;
                    padding: 0.5rem 1.6rem;
                    border-radius: 50px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; }

                /* ===== ALERT ERROR ===== */
                .alert-error-charte {
                    border: none;
                    background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px;
                    border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte {
                    position: relative;
                    width: 80px; height: 80px;
                    margin: 0 auto;
                }
                .loading-ring-charte {
                    position: absolute; inset: 0;
                    border-radius: 50%;
                    border: 4px solid #e2e8f0;
                    border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute;
                    top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px;
                    color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 992px) {
                    .header-modern { padding: 1rem; }
                    .hero-stats-charte { justify-content: flex-start; margin-top: 1rem; }
                    .hero-stat-divider { height: 30px; }
                }
                @media (max-width: 768px) {
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .hero-avatar-charte { width: 52px; height: 52px; font-size: 1.2rem; }
                    .hero-name-charte { font-size: 1.15rem; }
                    .hero-stats-charte { gap: 1rem; }
                    .hero-stat-value-charte { font-size: 1.2rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </React.Fragment>
    );
}